import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2, Printer, RotateCcw } from "lucide-react";
import DeleteButton from "@/components/DeleteButton";
import {
  CATEGORY_LABEL, DELAY_LABEL, REPORT_STATUS, STATUS_LABEL, STATUS_TONE, downloadCsv, duration, fmtDate,
  printTable, todayISO, useAuthImage,
} from "@/lib/dwr";

const Badge = ({ tone, children }) => <span className={`text-xs font-medium px-2.5 py-1 rounded-full border whitespace-nowrap ${tone}`}>{children}</span>;

function Photo({ url }) {
  const src = useAuthImage(url);
  return src
    ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="Work photo" className="w-24 h-24 rounded-lg object-cover border border-border" /></a>
    : <div className="w-24 h-24 rounded-lg bg-muted animate-pulse" />;
}

const lineColumns = [
  { label: "Date", value: (r) => r.report_date },
  { label: "Staff", value: (r) => r.staff_name },
  { label: "Department", value: (r) => r.department },
  { label: "Work / task", value: (r) => `${r.task_no ? `${r.task_no} ` : ""}${r.title}` },
  { label: "Customer / project", value: (r) => r.customer },
  { label: "Location", value: (r) => r.location },
  { label: "Time", value: (r) => (r.start_time ? `${r.start_time}–${r.end_time || ""}` : "") },
  { label: "Status", value: (r) => STATUS_LABEL[r.status] || r.status },
  { label: "Work done", value: (r) => r.description },
  { label: "Result", value: (r) => r.result },
  { label: "Challenges", value: (r) => r.challenges },
  { label: "Materials", value: (r) => r.materials_used },
  { label: "Follow-up", value: (r) => (r.follow_up_required ? r.follow_up_date || "Yes" : "") },
];

function printOne(report) {
  printTable({
    title: `Daily Work Report · ${report.staff_name}`,
    subtitle: `${fmtDate(report.report_date)} · ${report.department || ""} · ${REPORT_STATUS[report.status]?.label || report.status}`,
    columns: lineColumns.filter((c) => !["Date", "Staff", "Department"].includes(c.label)),
    rows: report.entries || [],
  });
}

function ReportDialog({ reportId, onClose, onReviewed }) {
  const [report, setReport] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/dwr/reports/${reportId}`).then(({ data }) => setReport(data)).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [reportId, onClose]);

  const review = async (action) => {
    if (action === "return" && comment.trim().length < 3) { toast.error("Say what needs correcting"); return; }
    setBusy(true);
    try {
      await api.post(`/dwr/reports/${reportId}/review`, { action, comment });
      toast.success(action === "approve" ? "Report approved" : "Returned for correction");
      onReviewed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {!report ? <Loader2 className="w-6 h-6 animate-spin m-auto" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">{report.staff_name} · {fmtDate(report.report_date)}</DialogTitle>
              <DialogDescription>
                {REPORT_STATUS[report.status]?.label}{report.late ? " · submitted late" : ""}
                {report.reviewed_by_name ? ` · reviewed by ${report.reviewed_by_name}` : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {(report.entries || []).map((e, i) => (
                <Card key={e.id} className="p-4 rounded-xl border border-border space-y-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <p className="font-medium">{i + 1}. {e.task_no ? `${e.task_no} · ` : ""}{e.title}</p>
                    <Badge tone={STATUS_TONE[e.status]}>{STATUS_LABEL[e.status]}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {[e.customer, e.location, e.start_time && `${e.start_time}–${e.end_time} (${duration(e.start_time, e.end_time)})`].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{e.description}</p>
                  {e.status === "delayed" && <p className="text-sm text-rose-700 dark:text-rose-400">Delay: {DELAY_LABEL[e.delay_reason]} · next action: {e.delay_responsible}</p>}
                  {[["Result", e.result], ["Challenges", e.challenges], ["Materials used", e.materials_used], ["Confirmed by", e.customer_confirmation], ["Remarks", e.remarks]]
                    .filter(([, v]) => v).map(([k, v]) => <p key={k} className="text-sm"><span className="text-muted-foreground">{k}: </span>{v}</p>)}
                  {e.follow_up_required && <p className="text-sm text-amber-700 dark:text-amber-400">Follow-up on {fmtDate(e.follow_up_date)}</p>}
                  {e.photos?.length > 0 && <div className="flex flex-wrap gap-2 pt-1">{e.photos.map((p) => <Photo key={p} url={p} />)}</div>}
                </Card>
              ))}
              {report.review_history?.length > 0 && (
                <ul className="text-xs text-muted-foreground space-y-0.5">
                  {report.review_history.map((h, i) => <li key={i}>{new Date(h.at).toLocaleString("en-GB")} · {h.by} · {h.action}{h.comment ? `: “${h.comment}”` : ""}</li>)}
                </ul>
              )}
              {report.can_review && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.16em]">Comment (required to return)</Label>
                  <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => printOne(report)}><Printer className="w-4 h-4 mr-1" /> Print / PDF</Button>
              <DeleteButton endpoint={`/dwr/reports/${report.id}`} what="report" onDeleted={onReviewed} />
              <div className="flex-1" />
              {report.can_review && (
                <>
                  <Button variant="outline" disabled={busy} onClick={() => review("return")} data-testid="review-return">
                    <RotateCcw className="w-4 h-4 mr-1" /> Return for correction
                  </Button>
                  <Button disabled={busy} onClick={() => review("approve")} className="kn-gradient-bg text-white border-0" data-testid="review-approve">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReviewTab() {
  const [day, setDay] = useState(todayISO());
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const [{ data: reports }, { data: dash }] = await Promise.all([
        api.get("/dwr/reports", { params: { date_from: day, date_to: day } }),
        api.get("/dwr/dashboard", { params: { date: day } }),
      ]);
      const submitted = reports.items.filter((r) => r.status !== "draft");
      const missing = dash.not_submitted.map((p) => ({ id: null, staff_name: p.name, department: p.department, status: "missing" }));
      setRows([...submitted.sort((a, b) => (a.status === "submitted" ? -1 : 1) - (b.status === "submitted" ? -1 : 1)), ...missing]);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  }, [day]);
  useEffect(() => { load(); }, [load]);
  const closeDialog = useCallback(() => setOpen(null), []);

  return (
    <div className="space-y-4">
      <Input type="date" value={day} max={todayISO()} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-auto" />
      {rows === null ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody is expected to report to you on this day.</p>
      ) : (
        <div className="grid gap-2">
          {rows.map((r, i) => (
            <Card key={r.id || `m${i}`} onClick={() => r.id && setOpen(r.id)} role={r.id ? "button" : undefined}
              className={`p-4 rounded-xl border border-border flex flex-wrap items-center justify-between gap-2 ${r.id ? "cursor-pointer hover:border-primary/40" : ""}`}>
              <div>
                <p className="font-medium">{r.staff_name}</p>
                <p className="text-xs text-muted-foreground">{r.department || "—"}{r.entries ? ` · ${r.entries.length} line(s)` : ""}{r.late ? " · late" : ""}</p>
              </div>
              <Badge tone={REPORT_STATUS[r.status].tone}>{REPORT_STATUS[r.status].label}</Badge>
            </Card>
          ))}
        </div>
      )}
      {open && <ReportDialog reportId={open} onClose={closeDialog} onReviewed={() => { setOpen(null); load(); }} />}
    </div>
  );
}

// ------------------------------------------------------------ reports

const REPORT_TYPES = {
  daily: "Daily Work Report",
  staff: "Staff Activity Report",
  department: "Department Report",
  technician: "Technician Report",
  customer: "Customer / Project Report",
  delayed: "Delayed Work Report",
  pending: "Pending Work Report",
  completed: "Completed Work Report",
  materials: "Material Usage Report",
  monthly: "Monthly Operational Summary",
};

const minutes = (e) => {
  if (!e.start_time || !e.end_time) return 0;
  const [a, b] = [e.start_time, e.end_time].map((t) => t.split(":").map(Number));
  return Math.max(0, b[0] * 60 + b[1] - (a[0] * 60 + a[1]));
};
const hours = (m) => (m / 60).toFixed(1);

function summarise(groupKey, lines, reports) {
  const groups = {};
  for (const l of lines) {
    const k = groupKey(l) || "—";
    const g = (groups[k] ||= { key: k, lines: 0, completed: 0, delayed: 0, minutes: 0, reports: new Set(), followups: 0 });
    g.lines += 1;
    g.completed += l.status === "completed" ? 1 : 0;
    g.delayed += l.status === "delayed" ? 1 : 0;
    g.minutes += minutes(l);
    g.followups += l.follow_up_required ? 1 : 0;
    g.reports.add(l.report_id);
  }
  return Object.values(groups).map((g) => ({ ...g, reports: g.reports.size })).sort((a, b) => b.lines - a.lines);
}

function ReportsTab() {
  const { dwr } = useAuth();
  const first = todayISO().slice(0, 8) + "01";
  const [type, setType] = useState("daily");
  const [from, setFrom] = useState(first);
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    const scope = dwr?.is_management ? "all" : "team";
    Promise.all([
      api.get("/dwr/reports", { params: { date_from: from, date_to: to } }),
      api.get("/dwr/tasks", { params: { scope } }),
      api.get("/dwr/staff"),
      type === "monthly" ? api.get("/dwr/dashboard", { params: { date: to, date_from: from, date_to: to } }) : Promise.resolve({ data: null }),
    ]).then(([r, t, s, d]) => setData({ reports: r.data.items.filter((x) => x.status !== "draft"), tasks: t.data.items, staff: s.data.items, dash: d.data }))
      .catch((e) => { toast.error(formatApiError(e)); setData({ reports: [], tasks: [], staff: [], dash: null }); });
  }, [type, from, to, dwr]);

  const table = useMemo(() => {
    if (!data) return null;
    const role = Object.fromEntries(data.staff.map((s) => [s.name, s.role]));
    const lines = data.reports.flatMap((r) => (r.entries || []).map((e) => ({ ...e, report_id: r.id, report_date: r.report_date, staff_name: r.staff_name, department: r.department })));
    const inRange = (iso) => iso && iso.slice(0, 10) >= from && iso.slice(0, 10) <= to;
    const taskCols = [
      { label: "Task", value: (t) => `${t.task_no} ${t.title}` }, { label: "Assigned to", value: (t) => t.assigned_to_name },
      { label: "Customer", value: (t) => t.customer }, { label: "Type", value: (t) => CATEGORY_LABEL[t.category] },
      { label: "Priority", value: (t) => t.priority }, { label: "Deadline", value: (t) => t.deadline },
      { label: "Status", value: (t) => STATUS_LABEL[t.status] + (t.overdue ? " (overdue)" : "") },
    ];
    const groupCols = (label) => [
      { label, value: (g) => g.key }, { label: "Reports", value: (g) => g.reports }, { label: "Work lines", value: (g) => g.lines },
      { label: "Completed", value: (g) => g.completed }, { label: "Delayed", value: (g) => g.delayed },
      { label: "Hours logged", value: (g) => hours(g.minutes) }, { label: "Follow-ups", value: (g) => g.followups },
    ];
    switch (type) {
      case "daily": return { columns: lineColumns, rows: lines };
      case "staff": return { columns: groupCols("Staff"), rows: summarise((l) => l.staff_name, lines) };
      case "department": return { columns: groupCols("Department"), rows: summarise((l) => l.department, lines) };
      case "technician": return { columns: groupCols("Technician"), rows: summarise((l) => l.staff_name, lines.filter((l) => role[l.staff_name] === "Technician")) };
      case "customer": return { columns: groupCols("Customer / project"), rows: summarise((l) => l.customer, lines) };
      case "delayed": return {
        columns: [...taskCols, { label: "Reason", value: (t) => DELAY_LABEL[t.delay_reason] }, { label: "Next action by", value: (t) => t.delay_responsible }],
        rows: data.tasks.filter((t) => t.status === "delayed"),
      };
      case "pending": return { columns: taskCols, rows: data.tasks.filter((t) => !["completed", "cancelled", "delayed"].includes(t.status)) };
      case "completed": return { columns: [...taskCols, { label: "Completed", value: (t) => (t.completed_at || "").slice(0, 10) }], rows: data.tasks.filter((t) => t.status === "completed" && inRange(t.completed_at)) };
      case "materials": return {
        columns: [lineColumns[0], lineColumns[1], lineColumns[3], lineColumns[4], { label: "Materials used", value: (r) => r.materials_used }],
        rows: lines.filter((l) => l.materials_used),
      };
      case "monthly": {
        const d = data.dash;
        const rows = d ? [
          ["Staff expected to report", d.staff_total], ["Daily reports submitted", data.reports.length],
          ["Work lines recorded", lines.length], ["Hours logged", hours(lines.reduce((s, l) => s + minutes(l), 0))],
          ["Tasks assigned", d.tasks_assigned], ["Tasks completed", d.tasks_completed], ["Open tasks overdue", d.overdue],
          ...Object.entries(d.completed_by_category).map(([k, v]) => [`Completed: ${CATEGORY_LABEL[k] || k}`, v]),
          ...Object.entries(d.open_by_status).map(([k, v]) => [`Open: ${STATUS_LABEL[k] || k}`, v]),
          ...Object.entries(d.delayed_by_reason).map(([k, v]) => [`Delayed because: ${DELAY_LABEL[k] || k}`, v]),
          ["Follow-ups due", d.follow_ups_due.length],
        ].map(([k, v]) => ({ k, v })) : [];
        return { columns: [{ label: "Measure", value: (r) => r.k }, { label: "Value", value: (r) => r.v }], rows };
      }
      default: return { columns: [], rows: [] };
    }
  }, [data, type, from, to]);

  const title = REPORT_TYPES[type];
  const subtitle = `${fmtDate(from)} – ${fmtDate(to)}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">Report</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-64" data-testid="report-type"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(REPORT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} /></div>
        <div className="flex-1" />
        <Button variant="outline" disabled={!table} onClick={() => printTable({ title, subtitle, ...table })}><Printer className="w-4 h-4 mr-1" /> Print / PDF</Button>
        <Button variant="outline" disabled={!table} onClick={() => downloadCsv(`${title.replace(/\W+/g, "_")}_${from}_${to}.csv`, table.columns, table.rows)}>
          <Download className="w-4 h-4 mr-1" /> Excel
        </Button>
      </div>
      {["delayed", "pending"].includes(type) && <p className="text-xs text-muted-foreground">Shows the current state of tasks; the date range doesn't apply.</p>}
      {!table ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : (
        <Card className="rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{table.columns.map((c) => <TableHead key={c.label} className="text-xs uppercase tracking-wider whitespace-nowrap">{c.label}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {table.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={table.columns.length} className="text-center text-sm text-muted-foreground py-8">No records for this selection.</TableCell></TableRow>
                ) : table.rows.slice(0, 500).map((r, i) => (
                  <TableRow key={i}>{table.columns.map((c) => <TableCell key={c.label} className="text-sm align-top max-w-xs">{c.value(r) ?? ""}</TableCell>)}</TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {table.rows.length > 500 && <p className="text-xs text-muted-foreground p-3">Showing 500 of {table.rows.length} rows. Print or Excel include them all.</p>}
        </Card>
      )}
    </div>
  );
}

export default function TeamReports() {
  return (
    <div className="space-y-5" data-testid="team-reports-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Team Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Review daily reports and run staff, delay and operational reports.</p>
      </div>
      <Tabs defaultValue="review">
        <TabsList><TabsTrigger value="review">Review</TabsTrigger><TabsTrigger value="reports">Reports</TabsTrigger></TabsList>
        <TabsContent value="review" className="mt-4"><ReviewTab /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
